import { ThemeProvider, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { BrowserRouter, useRoutes } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { MsalProvider } from '@azure/msal-react';
import { EventType, type AccountInfo } from '@azure/msal-browser';
import theme from './theme/theme';
import { store } from './store';
import FuseSettingsProvider from '@fuse/core/FuseSettings/FuseSettingsProvider';
import { AuthProvider } from './contexts/AuthContext';
import { SelectedOrganisationProvider } from './contexts/SelectedOrganisationContext';
import routesConfig from './config/routesConfig';
import msalInstance from './config/msalInstance';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useBlockNegativeInput } from './hooks/useBlockNegativeInput';
import { TruncatedCellTooltip } from './components/TruncatedCellTooltip';

// Handle account selection for multiple accounts
msalInstance.addEventCallback((event) => {
	if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
		const payload = event.payload as { account: AccountInfo };
		const account = payload.account;
		msalInstance.setActiveAccount(account);
	}
});

function AppRoutes() {
	return useRoutes(routesConfig);
}

const queryClient = new QueryClient();

function App() {
	useBlockNegativeInput();

	return (
		<BrowserRouter>
			<MsalProvider instance={msalInstance}>
				<ReduxProvider store={store}>
					<QueryClientProvider client={queryClient}>
						<AuthProvider>
							<SelectedOrganisationProvider>
								<FuseSettingsProvider>
									<LocalizationProvider dateAdapter={AdapterDateFns}>
										<ThemeProvider theme={theme}>
											<CssBaseline />
											<AppRoutes />
											<TruncatedCellTooltip />
										</ThemeProvider>
									</LocalizationProvider>
								</FuseSettingsProvider>
							</SelectedOrganisationProvider>
						</AuthProvider>
					</QueryClientProvider>
				</ReduxProvider>
			</MsalProvider>
		</BrowserRouter>
	);
}

export default App;
